import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Plus, Search, Star, Trash2 } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';

interface BrasilApiResult {
  cnpj: string;
  razaoSocial: string;
  nomeFantasia?: string;
  email?: string;
  telefone?: string;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  municipio?: string;
  uf?: string;
  cep?: string;
}

import { useAuth } from '@/contexts/AuthContext';
import { ApiError, apiFetch } from '@/lib/api';

export interface IssuerCompany {
  id: string;
  razaoSocial: string;
  nomeFantasia?: string | null;
  cnpj: string;
  inscricaoMunicipal: string;
  enderecoLogradouro: string;
  enderecoNumero: string;
  enderecoComplemento?: string | null;
  enderecoBairro: string;
  enderecoCidade: string;
  enderecoUf: string;
  enderecoCep: string;
  codigoIbgeMunicipio: string;
  email?: string | null;
  telefone?: string | null;
  opSimpNac: number;
  regEspTrib: number;
  verAplic: string;
  tpAmbDefault: number;
  serieDpsDefault: string;
  isDefault: boolean;
  isActive: boolean;
  smtpHost?: string | null;
  smtpPort?: number | null;
  smtpUser?: string | null;
  smtpFrom?: string | null;
}

type FormState = Omit<IssuerCompany, 'id' | 'isActive'> & { smtpPass?: string };

const EMPTY: FormState = {
  razaoSocial: '',
  nomeFantasia: '',
  cnpj: '',
  inscricaoMunicipal: '',
  enderecoLogradouro: '',
  enderecoNumero: '',
  enderecoComplemento: '',
  enderecoBairro: '',
  enderecoCidade: 'Jacareí',
  enderecoUf: 'SP',
  enderecoCep: '',
  codigoIbgeMunicipio: '3524402',
  email: '',
  telefone: '',
  opSimpNac: 1,
  regEspTrib: 0,
  verAplic: 'SimpleADM/0.5',
  tpAmbDefault: 2,
  serieDpsDefault: 'NFSE',
  isDefault: false,
  smtpHost: '',
  smtpPort: 587,
  smtpUser: '',
  smtpFrom: '',
  smtpPass: '',
};

function digits(s: string): string {
  return s.replace(/\D/g, '');
}

function formatCnpj(c: string): string {
  const d = digits(c);
  if (d.length !== 14) return d;
  return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
}

export function IssuerCompaniesPage() {
  const { user } = useAuth();
  const canManage = user?.role === 'SUPER_ADMIN';
  const qc = useQueryClient();
  const [editing, setEditing] = useState<IssuerCompany | 'new' | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['issuer-companies'],
    queryFn: () => apiFetch<IssuerCompany[]>('/issuer-companies?includeInactive=true'),
  });

  const setDefault = useMutation({
    mutationFn: (id: string) =>
      apiFetch<IssuerCompany>(`/issuer-companies/${id}/default`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['issuer-companies'] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/issuer-companies/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['issuer-companies'] }),
  });

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Empresas Emissoras</h1>
          <p className="text-sm text-muted-foreground">
            Cada CNPJ que emite NFSe pelo sistema. Clientes, contratos e financeiro são
            compartilhados; apenas a emissão da nota e o certificado A1 são por empresa.
          </p>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setEditing('new')}
            className="flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" /> Nova empresa
          </button>
        )}
      </header>

      {isLoading && <p className="text-sm text-muted-foreground">Carregando…</p>}
      {isError && <p className="text-sm text-destructive">Erro ao carregar.</p>}

      {data && data.length === 0 && (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border bg-card p-12 text-center">
          <Building2 className="h-10 w-10 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">Nenhuma empresa cadastrada.</p>
        </div>
      )}

      {data && data.length > 0 && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {data.map((c) => (
            <article
              key={c.id}
              className={`rounded-lg border bg-card p-4 space-y-3 ${
                c.isActive ? 'border-border' : 'border-muted opacity-60'
              }`}
            >
              <header className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-medium truncate">{c.razaoSocial}</h3>
                  {c.nomeFantasia && (
                    <p className="text-xs text-muted-foreground truncate">{c.nomeFantasia}</p>
                  )}
                  <p className="text-xs font-mono text-muted-foreground mt-0.5">
                    {formatCnpj(c.cnpj)}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {c.isDefault && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                      <Star className="h-3 w-3" /> Padrão
                    </span>
                  )}
                  {!c.isActive && (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
                      Inativa
                    </span>
                  )}
                </div>
              </header>

              <dl className="space-y-1 text-xs">
                <div className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">IM</dt>
                  <dd className="font-mono">{c.inscricaoMunicipal}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">IBGE</dt>
                  <dd className="font-mono">{c.codigoIbgeMunicipio}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">Endereço</dt>
                  <dd className="text-right truncate max-w-[60%]">
                    {c.enderecoCidade}/{c.enderecoUf}
                  </dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">Ambiente DPS</dt>
                  <dd>
                    {c.tpAmbDefault === 1 ? (
                      <span className="text-emerald-600 font-medium">Produção</span>
                    ) : (
                      <span className="text-amber-600">Homologação</span>
                    )}
                  </dd>
                </div>
              </dl>

              <div className="flex items-center gap-2 pt-1">
                {canManage && (
                  <button
                    type="button"
                    onClick={() => setEditing(c)}
                    className="rounded-md border border-input px-2 py-1 text-xs hover:bg-accent"
                  >
                    Editar
                  </button>
                )}
                {canManage && !c.isDefault && c.isActive && (
                  <button
                    type="button"
                    onClick={() => setDefault.mutate(c.id)}
                    className="flex items-center gap-1 rounded-md border border-input px-2 py-1 text-xs hover:bg-accent"
                  >
                    <Star className="h-3 w-3" /> Tornar padrão
                  </button>
                )}
                {canManage && !c.isDefault && (
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm(`Excluir empresa "${c.razaoSocial}"?`)) remove.mutate(c.id);
                    }}
                    className="ml-auto rounded-md p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      {editing && (
        <UpsertDialog
          company={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ['issuer-companies'] });
          }}
        />
      )}
    </div>
  );
}

function UpsertDialog({
  company,
  onClose,
  onSaved,
}: {
  company: IssuerCompany | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [lookupErr, setLookupErr] = useState<string | null>(null);

  const lookup = useMutation({
    mutationFn: (cnpj: string) =>
      apiFetch<BrasilApiResult>(`/clients/lookup-cnpj/${digits(cnpj)}`),
    onSuccess: (r) => {
      setForm((s) => ({
        ...s,
        razaoSocial: r.razaoSocial || s.razaoSocial,
        nomeFantasia: r.nomeFantasia ?? s.nomeFantasia,
        email: r.email ?? s.email,
        telefone: r.telefone ?? s.telefone,
        enderecoLogradouro: r.logradouro ?? s.enderecoLogradouro,
        enderecoNumero: r.numero ?? s.enderecoNumero,
        enderecoComplemento: r.complemento ?? s.enderecoComplemento,
        enderecoBairro: r.bairro ?? s.enderecoBairro,
        enderecoCidade: r.municipio ?? s.enderecoCidade,
        enderecoUf: r.uf ?? s.enderecoUf,
        enderecoCep: r.cep ?? s.enderecoCep,
      }));
      setLookupErr(null);
    },
    onError: () => setLookupErr('CNPJ não encontrado na BrasilAPI.'),
  });

  useEffect(() => {
    if (company) {
      const { id: _id, isActive: _ia, ...rest } = company;
      void _id;
      void _ia;
      setForm({ ...EMPTY, ...rest });
    } else {
      setForm(EMPTY);
    }
  }, [company]);

  const mutation = useMutation({
    mutationFn: () => {
      // Whitelist explicita: so envia campos aceitos pelo DTO do backend.
      // Evita mandar createdAt/updatedAt/smtpPass* (que vem do GET).
      const payload: Record<string, unknown> = {
        razaoSocial: form.razaoSocial,
        nomeFantasia: form.nomeFantasia || undefined,
        cnpj: digits(form.cnpj),
        inscricaoMunicipal: form.inscricaoMunicipal,
        enderecoLogradouro: form.enderecoLogradouro,
        enderecoNumero: form.enderecoNumero,
        enderecoComplemento: form.enderecoComplemento || undefined,
        enderecoBairro: form.enderecoBairro,
        enderecoCidade: form.enderecoCidade,
        enderecoUf: form.enderecoUf,
        enderecoCep: digits(form.enderecoCep),
        codigoIbgeMunicipio: digits(form.codigoIbgeMunicipio),
        email: form.email || undefined,
        telefone: form.telefone ? digits(form.telefone) : undefined,
        opSimpNac: form.opSimpNac,
        regEspTrib: form.regEspTrib,
        verAplic: form.verAplic,
        tpAmbDefault: form.tpAmbDefault,
        serieDpsDefault: form.serieDpsDefault,
        isDefault: form.isDefault,
        smtpHost: form.smtpHost || undefined,
        smtpPort: form.smtpPort || undefined,
        smtpUser: form.smtpUser || undefined,
        smtpFrom: form.smtpFrom || undefined,
        // Senha SMTP: so manda quando preenchida (deixa em branco = manter atual)
        smtpPass: form.smtpPass ? form.smtpPass : undefined,
      };
      if (company) {
        return apiFetch<IssuerCompany>(`/issuer-companies/${company.id}`, {
          method: 'PATCH',
          body: JSON.stringify(payload),
        });
      }
      return apiFetch<IssuerCompany>('/issuer-companies', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
    onSuccess: onSaved,
    onError: (err) =>
      setError(err instanceof ApiError ? err.body : err instanceof Error ? err.message : 'erro'),
  });

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((s) => ({ ...s, [key]: value }));
  }

  function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    mutation.mutate();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-medium">
            {company ? `Editar ${company.razaoSocial}` : 'Nova empresa emissora'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Section title="Identificação">
            <Field label="Razão social *">
              <input
                required
                className="input"
                value={form.razaoSocial}
                onChange={(e) => set('razaoSocial', e.target.value)}
              />
            </Field>
            <Field label="Nome fantasia">
              <input
                className="input"
                value={form.nomeFantasia ?? ''}
                onChange={(e) => set('nomeFantasia', e.target.value)}
              />
            </Field>
            <Field label="CNPJ (14 dígitos) *">
              <div className="flex gap-2">
                <input
                  required
                  inputMode="numeric"
                  className="input font-mono"
                  value={form.cnpj}
                  onChange={(e) => set('cnpj', e.target.value)}
                />
                <button
                  type="button"
                  disabled={lookup.isPending || digits(form.cnpj).length !== 14}
                  onClick={() => lookup.mutate(form.cnpj)}
                  title="Buscar dados do CNPJ na BrasilAPI"
                  className="flex items-center gap-1 rounded-md border border-input bg-background px-3 text-xs whitespace-nowrap hover:bg-accent disabled:opacity-50"
                >
                  <Search className="h-3 w-3" /> {lookup.isPending ? 'Buscando…' : 'Buscar'}
                </button>
              </div>
              {lookupErr && <span className="text-xs text-destructive">{lookupErr}</span>}
            </Field>
            <Field label="Inscrição municipal *">
              <input
                required
                className="input"
                value={form.inscricaoMunicipal}
                onChange={(e) => set('inscricaoMunicipal', e.target.value)}
              />
            </Field>
          </Section>

          <Section title="Endereço">
            <Field label="Logradouro *">
              <input
                required
                className="input"
                value={form.enderecoLogradouro}
                onChange={(e) => set('enderecoLogradouro', e.target.value)}
              />
            </Field>
            <Field label="Número *">
              <input
                required
                className="input"
                value={form.enderecoNumero}
                onChange={(e) => set('enderecoNumero', e.target.value)}
              />
            </Field>
            <Field label="Complemento">
              <input
                className="input"
                value={form.enderecoComplemento ?? ''}
                onChange={(e) => set('enderecoComplemento', e.target.value)}
              />
            </Field>
            <Field label="Bairro *">
              <input
                required
                className="input"
                value={form.enderecoBairro}
                onChange={(e) => set('enderecoBairro', e.target.value)}
              />
            </Field>
            <Field label="Cidade *">
              <input
                required
                className="input"
                value={form.enderecoCidade}
                onChange={(e) => set('enderecoCidade', e.target.value)}
              />
            </Field>
            <Field label="UF *">
              <input
                required
                maxLength={2}
                className="input uppercase"
                value={form.enderecoUf}
                onChange={(e) => set('enderecoUf', e.target.value.toUpperCase())}
              />
            </Field>
            <Field label="CEP (8 dígitos) *">
              <input
                required
                inputMode="numeric"
                className="input font-mono"
                value={form.enderecoCep}
                onChange={(e) => set('enderecoCep', e.target.value)}
              />
            </Field>
            <Field label="Código IBGE município (7 dígitos) *">
              <input
                required
                inputMode="numeric"
                className="input font-mono"
                value={form.codigoIbgeMunicipio}
                onChange={(e) => set('codigoIbgeMunicipio', e.target.value)}
              />
              <SefinStatusBadge ibgeCode={form.codigoIbgeMunicipio} />
            </Field>
          </Section>

          <Section title="Contato">
            <Field label="E-mail">
              <input
                type="email"
                className="input"
                value={form.email ?? ''}
                onChange={(e) => set('email', e.target.value)}
              />
            </Field>
            <Field label="Telefone">
              <input
                className="input"
                value={form.telefone ?? ''}
                onChange={(e) => set('telefone', e.target.value)}
              />
            </Field>
          </Section>

          <Section title="Regime tributário e DPS">
            <Field label="Regime Simples Nacional">
              <select
                className="input"
                value={form.opSimpNac}
                onChange={(e) => set('opSimpNac', Number(e.target.value))}
              >
                <option value={1}>1 — Não Optante</option>
                <option value={2}>2 — MEI</option>
                <option value={3}>3 — ME/EPP</option>
              </select>
            </Field>
            <Field label="Regime Especial de Tributação">
              <select
                className="input"
                value={form.regEspTrib}
                onChange={(e) => set('regEspTrib', Number(e.target.value))}
              >
                <option value={0}>0 — Sem regime especial</option>
                <option value={1}>1 — Microempresa Municipal</option>
                <option value={2}>2 — Estimativa</option>
                <option value={3}>3 — Sociedade de Profissionais</option>
                <option value={4}>4 — Cooperativa</option>
                <option value={5}>5 — MEI (municipal)</option>
                <option value={6}>6 — ME/EPP (municipal)</option>
              </select>
            </Field>
            <Field label="Ambiente DPS padrão">
              <select
                className="input"
                value={form.tpAmbDefault}
                onChange={(e) => set('tpAmbDefault', Number(e.target.value))}
              >
                <option value={2}>2 — Homologação</option>
                <option value={1}>1 — Produção</option>
              </select>
            </Field>
            <Field label="Série DPS">
              <input
                maxLength={5}
                className="input"
                value={form.serieDpsDefault}
                onChange={(e) => set('serieDpsDefault', e.target.value)}
              />
            </Field>
          </Section>

          {/* ===================== SMTP ===================== */}
          <Section title="E-mail (SMTP) — opcional">
            <Field label="Servidor SMTP">
              <input
                className="input"
                placeholder="smtp.gmail.com"
                value={form.smtpHost ?? ''}
                onChange={(e) => set('smtpHost', e.target.value)}
              />
            </Field>
            <Field label="Porta">
              <input
                type="number"
                className="input"
                placeholder="587"
                value={form.smtpPort ?? ''}
                onChange={(e) =>
                  set('smtpPort', e.target.value ? Number(e.target.value) : null)
                }
              />
            </Field>
            <Field label="Usuário">
              <input
                className="input"
                placeholder="cobranca@empresa.com"
                value={form.smtpUser ?? ''}
                onChange={(e) => set('smtpUser', e.target.value)}
              />
            </Field>
            <Field label="Senha (cifrada no banco)">
              <input
                type="password"
                className="input font-mono"
                placeholder={company ? 'manter a senha atual (deixe vazio)' : ''}
                value={form.smtpPass ?? ''}
                onChange={(e) => set('smtpPass', e.target.value)}
              />
            </Field>
            <Field label="De (From)">
              <input
                className="input"
                placeholder="Simple Hotel <cobranca@empresa.com>"
                value={form.smtpFrom ?? ''}
                onChange={(e) => set('smtpFrom', e.target.value)}
              />
            </Field>
            {company && form.smtpHost && (
              <Field label="Testar envio">
                <TestSmtpButton companyId={company.id} />
              </Field>
            )}
          </Section>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isDefault}
              onChange={(e) => set('isDefault', e.target.checked)}
            />
            Definir como empresa padrão
          </label>

          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {mutation.isPending ? 'Salvando…' : company ? 'Salvar' : 'Criar empresa'}
            </button>
          </div>
        </form>

        <style>{`
          .input {
            width: 100%;
            border: 1px solid hsl(var(--input));
            background: hsl(var(--background));
            padding: 0.5rem 0.75rem;
            border-radius: 0.375rem;
            font-size: 0.875rem;
          }
          .input:focus { outline: none; box-shadow: 0 0 0 2px hsl(var(--ring) / 0.4); }
        `}</style>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="space-y-3">
      <legend className="text-sm font-medium text-muted-foreground">{title}</legend>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">{children}</div>
    </fieldset>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="space-y-1 block">
      <span className="text-xs font-medium">{label}</span>
      {children}
    </label>
  );
}

type SefinStatusValue = 'ATIVO' | 'ADERIDO' | 'NAO_ADERIDO' | 'DESCONHECIDO';

const SEFIN_STATUS_META: Record<SefinStatusValue, { label: string; color: string; hint: string }> = {
  ATIVO: {
    label: '✓ ATIVO',
    color: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30',
    hint: 'Municipio emite NFSe via SEFIN Nacional — vai funcionar sem ajustes.',
  },
  ADERIDO: {
    label: '⏳ ADERIDO (em transicao)',
    color: 'bg-amber-500/10 text-amber-700 border-amber-500/30',
    hint: 'Convenio assinado mas plataforma ainda nao opera. Emissao pode falhar ate municipio ativar.',
  },
  NAO_ADERIDO: {
    label: '✗ NAO ADERIDO',
    color: 'bg-destructive/10 text-destructive border-destructive/30',
    hint: 'Municipio nao integrou ao SEFIN Nacional. Emissao via nosso sistema NAO funcionara.',
  },
  DESCONHECIDO: {
    label: '? DESCONHECIDO',
    color: 'bg-muted text-muted-foreground border-border',
    hint: 'IBGE invalido ou nao listado na base oficial.',
  },
};

/**
 * Badge que mostra o status SEFIN do municipio em tempo real.
 * Consulta /municipalities/:ibge/sefin-status quando o IBGE tem 7 digitos.
 */
function SefinStatusBadge({ ibgeCode }: { ibgeCode: string }) {
  const cleaned = ibgeCode.replace(/\D/g, '');
  const enabled = cleaned.length === 7;

  const { data, isLoading } = useQuery({
    queryKey: ['sefin-status', cleaned],
    queryFn: () =>
      apiFetch<{ ibgeCode: string; status: SefinStatusValue }>(
        `/municipalities/${cleaned}/sefin-status`,
      ),
    enabled,
    staleTime: 24 * 60 * 60 * 1000, // 1 dia em cache
  });

  if (!enabled) return null;
  if (isLoading || !data) {
    return (
      <p className="mt-1 text-[10px] text-muted-foreground">Consultando base oficial SEFIN…</p>
    );
  }
  const meta = SEFIN_STATUS_META[data.status];
  return (
    <div
      className={`mt-1 inline-flex flex-col gap-0.5 rounded-md border px-2 py-1 ${meta.color}`}
    >
      <span className="text-[10px] font-semibold uppercase tracking-wider">{meta.label}</span>
      <span className="text-[10px] leading-tight opacity-90">{meta.hint}</span>
    </div>
  );
}

/**
 * Botao que dispara um e-mail de teste via SMTP cadastrado na empresa.
 * Aparece somente quando ja existe SMTP salvo (modo edicao).
 */
function TestSmtpButton({ companyId }: { companyId: string }) {
  const [to, setTo] = useState('');
  const [result, setResult] = useState<{ sent: boolean; reason?: string } | null>(null);
  const test = useMutation({
    mutationFn: () =>
      apiFetch<{ sent: boolean; reason?: string }>(
        `/issuer-companies/${companyId}/test-smtp`,
        {
          method: 'POST',
          body: JSON.stringify({ to }),
        },
      ),
    onSuccess: (r) => setResult(r),
    onError: (err) =>
      setResult({
        sent: false,
        reason: err instanceof Error ? err.message : 'erro',
      }),
  });
  return (
    <div className="space-y-1">
      <div className="flex gap-2">
        <input
          type="email"
          className="input flex-1 text-xs"
          placeholder="seu-email@destino.com"
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
        <button
          type="button"
          disabled={!to || test.isPending}
          onClick={() => {
            setResult(null);
            test.mutate();
          }}
          className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {test.isPending ? 'Enviando…' : 'Testar'}
        </button>
      </div>
      {result && (
        <p
          className={`text-[10px] ${result.sent ? 'text-emerald-600' : 'text-destructive'}`}
        >
          {result.sent ? '✓ E-mail enviado com sucesso' : `✗ Falhou: ${result.reason}`}
        </p>
      )}
    </div>
  );
}
